import { useEffect, useState } from 'react';
import { Option } from '@streem/ui-react';
import { getPreferredAudioInputId, setPreferredAudioInputId } from '@streem/toolbox';
import appLogger from '../util/logging/app_logger';

const log = appLogger.extend('AudioInputSelect');

type AudioInputOption = Option<string>;

type AudioInputPreferredDevicesReturnValues = {
    selectedAudioInputDevice: AudioInputOption;
    setSelectedAudioInputDevice: React.Dispatch<React.SetStateAction<AudioInputOption>>;
    initialized: boolean;
};

interface UseAudioDefaultsProps {
    audioInputDevices: AudioInputOption[];
}
/**
 * Gets called when there is no preferred mic device.
 *
 * This function will set the preferred mic device to be the mic device that has an active track
 * when getUserMedia has been called.
 *
 * This is helpful in situations when users open device settings modal and do not select an item from the dropdown.
 * Some users assume that because the dropdown has a value, they don't have to perform an initial selection.
 */
async function setActiveAudioInputDevice(audioInputDevices: AudioInputOption[]): Promise<void> {
    try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const activeTrack = mediaStream.getAudioTracks().find(t => t.readyState === 'live');
        if (!activeTrack) {
            log.warn('No active audio track found');
            return;
        }

        const activeDevice = audioInputDevices.find(d => d.label === activeTrack.label);
        if (!activeDevice) {
            log.warn('No matching audio input device found for active track');
            return;
        }

        setPreferredAudioInputId(activeDevice.value);
    } catch (error) {
        log.warn('Call to getUserMedia failed', error);
    }
}

export const useAudioInputPreferredDevices = (
    props: UseAudioDefaultsProps,
): AudioInputPreferredDevicesReturnValues => {
    const { audioInputDevices } = props;
    const [selectedAudioInputDevice, setSelectedAudioInputDevice] = useState<
        AudioInputOption | undefined
    >(undefined);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (audioInputDevices.length > 0) {
            const preferredAudioInputId = getPreferredAudioInputId();
            if (preferredAudioInputId) {
                const preferredAudioInput = audioInputDevices.find(
                    device => device.value === preferredAudioInputId,
                );
                if (preferredAudioInput) {
                    setSelectedAudioInputDevice(preferredAudioInput);
                } else {
                    const defaultInputDevice = audioInputDevices.find(
                        device => device.value === 'default',
                    );

                    if (defaultInputDevice) {
                        log.info(
                            `Could not find preferred preferredAudioInputId=${preferredAudioInputId}
                        Setting to default instead: ${JSON.stringify(defaultInputDevice)}`,
                        );
                        setPreferredAudioInputId('default');
                    } else {
                        log.error('Unexpected state: Default Audio Input device not found!');
                    }
                }
            } else {
                setActiveAudioInputDevice(audioInputDevices);
            }

            setInitialized(true);
        }
    }, [audioInputDevices]);

    return {
        selectedAudioInputDevice,
        setSelectedAudioInputDevice,
        initialized,
    };
};
